import { useCallback, useEffect, useState } from 'react';
import { Button, Input, Toggle, MAGNETIC_TOKENS, Select, Collapse, Icon, css } from '@appd/uik';
import { capitalizeFirstLetter } from '../../../../../utils';
import { isEqual, values } from 'lodash';
import { deepMerge } from '../../../../../utils/deepMerge';
import { PropertiesPanelContentProps } from '../../models';
import { jsonataWithCallback } from '../../../../../modules';
import { getJsonataExpressionForVariable } from '../../utils';
import { keys } from 'lodash';
import { logger } from '../../../../../utils';
import { ExpressionJson } from '../../../../models';

interface FilterPropertiesPanelContentProps extends PropertiesPanelContentProps {
  expressions?: Record<string, ExpressionJson>;
  queryResult?: Record<string, unknown>;
}

export interface FilterPropertiesPanelProps extends PropertiesPanelContentProps {
  expressions?: Record<string, ExpressionJson>;
}

export const FilterSelectionOption = {
  'MULTI-VALUE': 1,
  'SINGLE-VALUE': 0,
} as const;
export type FilterSelectionOptionType = (typeof FilterSelectionOption)[keyof typeof FilterSelectionOption];

export const FilterDefaultOption = {
  NONE: 'None',
  CUSTOM: 'Choose Default',
  FIRST: 'First Value',
} as const;
export type FilterDefaultOptionType = (typeof FilterDefaultOption)[keyof typeof FilterDefaultOption];

interface StaticOption {
  label: string;
  value: string;
  id: string;
}

const {
  TYPOGRAPHY: { P3_EMPHASIS },
} = MAGNETIC_TOKENS;

const EXP_WRAP_INDEX = 3;

export const FilterPropertiesPanelContent = ({
  widget,
  updateWidget,
  expressions,
  queryResult,
}: FilterPropertiesPanelContentProps): JSX.Element => {
  const [filterProps, setFilterProps] = useState<Record<string, any>>(widget?.component?.props ?? {});
  const [filterOptions, setFilterOptions] = useState<string>(filterProps?.options ?? '');
  const [title, setTitle] = useState<string>(filterProps?.title ?? '');
  const [filterKey, setFilterKey] = useState<string>(filterProps?.filterKey ?? '');
  const [varName, setVarName] = useState<string>(filterProps?.varName ?? '');
  const [multiSelect, setMultiSelect] = useState<boolean>(filterProps?.multiple ?? true);
  const [initialValue, setInitialValue] = useState<number[]>((filterProps?.initialValue as number[]) ?? []);
  const [defaultOption, setDefaultOption] = useState<FilterDefaultOptionType>(
    (filterProps?.defaultOption as FilterDefaultOptionType) ?? FilterDefaultOption.NONE,
  );
  const [selectionOptionOpen, setSelectionOptionOpen] = useState<boolean>(true);
  const [staticOptions, setStaticOptions] = useState<StaticOption[]>([]);
  const [data, setData] = useState<Record<string, any>>({});

  const updateStaticOptions = useCallback((widgetOptions: StaticOption[]) => {
    const hasAppendedOptions = String(widgetOptions).match(/\[\{.*?\}\]/);
    if (hasAppendedOptions) {
      const appendedOptions = JSON.parse(hasAppendedOptions[0]);
      setStaticOptions(appendedOptions);
    } else {
      setStaticOptions([]);
    }
  }, []);

  useEffect(() => {
    setFilterProps(widget?.component.props ?? {});
    updateStaticOptions(widget.component.props.options ?? '');
  }, [widget]);

  const addOption = () => {
    setStaticOptions(staticOptions => [
      ...staticOptions,
      { label: '', value: '', id: Math.random().toString(36).substring(2, 7) },
    ]);
  };

  const removeOption = (id: string) => {
    const newStaticOptions = staticOptions.filter(option => option.id !== id);
    setStaticOptions(newStaticOptions);
    updateFilterOptions(newStaticOptions);
  };

  useEffect(() => {
    setTitle(filterProps?.title ?? '');
    setMultiSelect(filterProps?.multiple ?? true);
    setFilterKey(filterProps?.filterKey ?? '');
    setVarName(filterProps?.varName ?? '');
    setInitialValue((filterProps?.initialValue as number[]) ?? []);
  }, [filterProps]);

  const handleOnChangeUpdateFilterOption = () => {
    const newStaticOptions = staticOptions.filter(option => option.label !== '' || option.value !== '');
    updateFilterOptions(newStaticOptions);
  };

  useEffect(() => {
    updateStaticOptions(widget.component.props.options);
  }, []);

  useEffect(() => {
    const evaluate = async () => {
      try {
        if (filterOptions && expressions) {
          const key = filterOptions.split('.')[1].match(/^\w+/);
          if (key && key.length) {
            const selectedQueryExpression = expressions[key[0]];
            const res: any = await jsonataWithCallback(
              selectedQueryExpression.jsonata.substring(EXP_WRAP_INDEX, selectedQueryExpression.jsonata.length - 1),
              queryResult,
            );
            setData(res);
          }
        }
      } catch (e: any) {
        logger.log('there is an error fetching data for filter initial value dropdown: ', e);
      }
    };
    evaluate();
  }, [expressions, queryResult, filterOptions]);

  const updateFilterProps = (newProps: Record<string, any>) => {
    const newFilterTemplateProps = deepMerge({}, newProps, filterProps);

    if (!isEqual(filterProps, newFilterTemplateProps)) {
      updateWidget({
        component: { name: widget?.component?.name ?? 'editable:Filter', props: newFilterTemplateProps },
      });
      setFilterProps(newFilterTemplateProps);
    }
  };

  const removeDuplicatesById = (filterOptions: { id: string }[]) => {
    const seenIds = new Set();
    return filterOptions.filter(({ id }) => !seenIds.has(id) && seenIds.add(id));
  };

  const getExpressionVariableFromFilterOptions = (filterOption: string) => {
    let expressionVariable;
    const hasAddedStaticOptions = String(filterOption).match(/\[\{.*?\}\]/);
    if (hasAddedStaticOptions) {
      const addedStaticOptions = JSON.parse(hasAddedStaticOptions[0]);
      if (addedStaticOptions.length > 0) {
        const hasOriginalFilter = filterOption.match(/,\s*\[\s*([^\[\]]+)\s*\]\s*\)\}/);
        const originalFilter = hasOriginalFilter ? hasOriginalFilter[1] : null;
        expressionVariable = originalFilter?.split('.')[1];
      }
    } else {
      const match = String(filterOption).match(/\/\$\{\s*expressions\.(\w+)\s*\}/);
      expressionVariable = match ? match[1] : null;
    }
    return expressionVariable;
  };

  const updateFilterOptions = (newFilterOptions: any) => {
    const originalOptions = filterProps.options;
    let originalFilter = originalOptions?.slice(3, -1);
    let allOptions = newFilterOptions;
    // Check if there is newly added option already
    const hasAddedStaticOptions = String(originalOptions).match(/\[\{.*?\}\]/);
    if (hasAddedStaticOptions) {
      const addedStaticOptions = JSON.parse(hasAddedStaticOptions[0]);
      if (addedStaticOptions.length > 0) {
        const hasOriginalFilter = originalOptions.match(/,\s*\[\s*([^\[\]]+)\s*\]\s*\)\}/);
        originalFilter = hasOriginalFilter ? hasOriginalFilter[1] : null;
        allOptions = removeDuplicatesById([...newFilterOptions]);
      }
    }

    const stringifyAllOptions = JSON.stringify(allOptions);
    let newOptions = '';

    if (allOptions.length === 0) {
      newOptions = originalFilter?.length > 0 ? `/\${ ${originalFilter} }` : `/\${[]}`;
    } else {
      if (originalFilter) {
        newOptions = `/\${ $append(${stringifyAllOptions}, [${originalFilter}])}`;
      } else {
        newOptions = `/\${ $append(${stringifyAllOptions}, [])}`;
      }
    }
    updateFilterProps({ options: newOptions });
  };

  const onMultiSelectionChange = ([index]: number[]) => {
    const option = Object.values(FilterSelectionOption)[index] as FilterSelectionOptionType;
    const isMultileSelect = Boolean(option);
    const newFilterProps = {
      multiple: isMultileSelect,
      ...(isMultileSelect && !initialValue?.length ? {} : { initialValue: [initialValue[0]] }),
    };
    setMultiSelect(isMultileSelect);
    updateFilterProps(newFilterProps);
  };

  const onDefaultOptionChange = ([index]: number[]) => {
    const option = Object.values(FilterDefaultOption)[index] as FilterDefaultOptionType;
    setDefaultOption(option);
    let preset: number[] = [];
    switch (option) {
      case FilterDefaultOption.FIRST:
        preset = data?.length ? [data[0].value] : [];
        break;
      case FilterDefaultOption.CUSTOM:
        preset = initialValue ?? [];
        break;
      case FilterDefaultOption.NONE:
      default:
        break;
    }
    setInitialValue(preset);
    updateFilterProps({
      defaultOption: option,
      initialValue: preset,
    });
  };

  const onInitialValueChange = (values: number[]) => {
    const preset = values.map(value => data[value].value);
    setInitialValue(preset);
    updateFilterProps({
      initialValue: preset,
    });
  };

  return (
    <div data-testid="filter-properties" key="filterProperties" className="flex flex-col h-full gap-4">
      <Collapse.Group open={[true]}>
        <Collapse title="General">
          <Collapse.Panel>
            <Input
              key="VarName"
              style={{ marginBottom: '10px' }}
              label="Variable Name"
              value={varName}
              onChange={e => {
                setVarName(e.target.value);
                updateFilterProps({
                  varName: e.target.value,
                  value: '/${ ' + ['globals.variables.filters', e.target.value, 'value'].join('.') + ' }',
                });
              }}
              required={true}
            />

            <Input
              key="title"
              style={{ marginBottom: '10px' }}
              label="Display Name"
              value={title}
              onChange={e => {
                setTitle(e.target.value);
                updateFilterProps({ title: e.target.value });
              }}
              required={true}
            />

            <Input
              key="filterKey"
              style={{ marginBottom: '10px' }}
              label="Filter Key"
              value={filterKey}
              onChange={e => {
                setFilterKey(e.target.value);
                updateFilterProps({ filterKey: e.target.value });
              }}
              required={true}
            />
          </Collapse.Panel>
        </Collapse>
      </Collapse.Group>

      <Collapse.Group open={[true]}>
        <Collapse title="Data Binding">
          <Collapse.Panel>
            <div key="data-binding" className="grid mb-1 gap-4">
              <Select
                label="Options"
                value={[
                  keys(expressions).findIndex(
                    variable =>
                      (getExpressionVariableFromFilterOptions(filterOptions) ?? '').trim().toLocaleLowerCase() ===
                      variable.toLowerCase(),
                  ),
                ]}
                onChange={(value: number[]) => {
                  const filterOptions = widget.component.props.options;
                  const expressionVariable = keys(expressions)[value[0]];
                  const expression = `expressions.${expressionVariable}.jsonata`;
                  const hasAppendedOptions = String(filterOptions).match(/\[\{.*?\}\]/);
                  let newOptions = filterOptions;
                  if (staticOptions.length > 0) {
                    newOptions = '/${ $append(' + JSON.stringify(staticOptions) + `, [` + expression + '])}';
                  } else if (hasAppendedOptions) {
                    const appendedOptions = JSON.parse(hasAppendedOptions[0]);
                    if (appendedOptions.length > 0) {
                      newOptions = '/${ $append(' + JSON.stringify(appendedOptions) + `, [` + expression + '])}';
                    }
                  } else {
                    newOptions = getJsonataExpressionForVariable(expressionVariable);
                  }
                  setFilterOptions(newOptions);
                  updateFilterProps({ options: newOptions });
                }}
                showClear={false}
                required
              >
                {keys(expressions).map(expression => (
                  <Select.Option key={expression}>{expression}</Select.Option>
                ))}
              </Select>
            </div>
          </Collapse.Panel>
        </Collapse>
      </Collapse.Group>

      <Collapse.Group open={[true]}>
        <Collapse title="Menu Options">
          <Collapse.Panel className={css({ padding: 8 })}>
            <div className="grid mb-1 gap-3 mt-1">
              <div className="text-sm font-semibold mb-2">Static menu configuration</div>
              {staticOptions.map((option, index) => (
                <div key={option.id} className="flex items-center space-x-2 mb-2">
                  <div className="flex items-center p-2 border-2 border-gray-300 rounded space-x-4">
                    <div className="flex flex-col flex-shrink-0">
                      <label className="text-sm font-semibold text-gray-700">Label</label>
                      <input
                        className="mt-1 border-2 border-gray-300 rounded px-2 py-1 text-sm w-20"
                        type="text"
                        placeholder="All"
                        value={option.label}
                        onChange={e => {
                          staticOptions[index].label = e.target.value;
                          setStaticOptions(staticOptions.filter(item => item));
                          handleOnChangeUpdateFilterOption();
                        }}
                      />
                    </div>
                    <div className="flex flex-col flex-grow">
                      <label className="text-sm font-semibold text-gray-700">Value</label>
                      <input
                        className="mt-1 border-2 border-gray-300 rounded px-2 py-1 text-sm"
                        type="text"
                        placeholder="*"
                        value={option.value}
                        onChange={e => {
                          staticOptions[index].value = e.target.value;
                          setStaticOptions(staticOptions.filter(item => item));
                          handleOnChangeUpdateFilterOption();
                        }}
                      />
                    </div>
                  </div>
                  <button onClick={() => removeOption(option.id)} className="p-1">
                    <Icon.Action.Disable size="MEDIUM" />
                  </button>
                </div>
              ))}
              <button
                style={{
                  border: '1px dashed #726F6F',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  fontSize: '16px',
                  cursor: 'pointer',
                  background: 'transparent',
                  width: '90%',
                }}
                onClick={addOption}
              >
                + Add
              </button>

              <div key="multiselect" className="grid gap-2">
                <div style={{ font: P3_EMPHASIS, marginTop: '10px' }}>Selection Options</div>
                <Toggle
                  style={{ marginLeft: '5px', marginBottom: '10px' }}
                  selected={[Object.values(FilterSelectionOption).findIndex(value => Boolean(value) === multiSelect)]}
                  onSelectedChange={onMultiSelectionChange}
                >
                  {Object.entries(FilterSelectionOption).map(([display, option]) => (
                    <Button key={option}>{capitalizeFirstLetter(display.toLowerCase())}</Button>
                  ))}
                </Toggle>
              </div>

              {data && data.length > 0 && (
                <div key="defaultvalueoption" className="grid gap-2">
                  <div style={{ font: P3_EMPHASIS, marginTop: '10px' }}>Default Value</div>
                  <Toggle
                    style={{ marginLeft: '5px', marginBottom: '10px' }}
                    selected={[Object.values(FilterDefaultOption).findIndex(value => value === defaultOption)]}
                    onSelectedChange={onDefaultOptionChange}
                  >
                    {Object.values(FilterDefaultOption).map(option => (
                      <Button key={option}>{capitalizeFirstLetter(option)}</Button>
                    ))}
                  </Toggle>
                </div>
              )}

              {defaultOption === FilterDefaultOption.CUSTOM && (
                <div key="defaultvalue" className="grid gap-2">
                  <Select
                    multiple={multiSelect}
                    showClear={false}
                    onChange={onInitialValueChange}
                    placeholder="Select a value"
                    required
                  >
                    {Object.values(data).map(option => (
                      <Select.Option key={option.value}>{option.label}</Select.Option>
                    ))}
                  </Select>
                </div>
              )}
            </div>
          </Collapse.Panel>
        </Collapse>
      </Collapse.Group>
    </div>
  );
};
